import { Button, ButtonGroup, Card, CardContent, IconButton, InputAdornment, MenuItem, TextField, Tooltip } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { AccountSelect, UserSelect } from "../../components/FinanceSelects";
import { CategorySearchSelect } from "../../components/CategorySearchSelect";
import { DateRangePickerField } from "../../components/DateRangePickerField";
import { FilterSelect } from "../../components/FilterSelect";
import { TagFilterSelect } from "../../components/TagFilterSelect";
import { appToday } from "../../lib/devDate";
import type { Account, Category, Member, Tag } from "../../lib/types";
import { defaultStatisticsFilters, recommendedStatisticsBucket } from "./dateRange";
import type { StatisticsFilters, StatisticsPreset } from "./types";

async function copyToClipboard(text: string) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Clipboard API can be unavailable in insecure contexts; use the fallback.
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  }
  area.remove();
  return copied;
}

function fieldAdornment(icon: ReactNode) {
  return (
    <InputAdornment position="start" className="record-field-adornment stat-field-adornment">
      {icon}
    </InputAdornment>
  );
}

type StatisticsFilterBarProps = {
  filters: StatisticsFilters;
  merchantInput: string;
  categories: Category[];
  members: Member[];
  accounts: Account[];
  tags: Tag[];
  activePreset: StatisticsPreset | null;
  monthlyMode?: boolean;
  compact?: boolean;
  hasExternalFilters?: boolean;
  onChange: (filters: StatisticsFilters) => void;
  onMerchantInput: (value: string) => void;
  onPreset: (preset: StatisticsPreset) => void;
  onReset: () => void;
};

export function StatisticsFilterBar({
  filters,
  merchantInput,
  categories,
  members,
  accounts,
  tags,
  activePreset,
  monthlyMode = false,
  compact = false,
  hasExternalFilters = false,
  onChange,
  onMerchantInput,
  onPreset,
  onReset,
}: StatisticsFilterBarProps) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const activeCount = monthlyMode
    ? [filters.userId, filters.accountId, filters.compare !== "previous" ? filters.compare : "", hasExternalFilters ? "category" : ""].filter(Boolean).length + (filters.tagIds.length ? 1 : 0)
    : [filters.type, filters.categoryId, filters.userId, filters.accountId, filters.merchant].filter(Boolean).length + (filters.tagIds.length ? 1 : 0);
  const canReset = monthlyMode
    ? hasExternalFilters || Boolean(filters.type || filters.categoryId || filters.userId || filters.accountId || filters.tagIds.length || filters.merchant || filters.compare !== "previous" || merchantInput !== filters.merchant)
    : JSON.stringify(filters) !== JSON.stringify(defaultStatisticsFilters(appToday())) || merchantInput !== filters.merchant;
  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  const shareView = async () => {
    if (await copyToClipboard(window.location.href)) {
      setCopied(true);
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1600);
    }
  };
  const presetButton = (preset: StatisticsPreset, label: string, mobileLabel: string) => (
    <Button type="button" variant={activePreset === preset ? "contained" : "outlined"} disableElevation aria-label={label} onClick={() => onPreset(preset)}>
      <span className="stat-period-label-desktop">{label}</span>
      <span className="stat-period-label-mobile" aria-hidden="true">{mobileLabel}</span>
    </Button>
  );

  const content = (
    <>
        <div className={`stat-filter-shortcuts${monthlyMode ? " monthly" : ""}`}>
          {!monthlyMode ? <ButtonGroup variant="outlined" size="small" aria-label="Statistics period shortcuts">
            {presetButton("month", "This month", "This M")}
            {presetButton("lastMonth", "Last month", "Last M")}
            {presetButton("quarter", "3 months", "3 M")}
            {presetButton("year", "YTD", "YTD")}
          </ButtonGroup> : compact ? null : <span className="statistics-scope-label">Monthly scope</span>}
          <div className="stat-filter-actions">
            <Tooltip title={copied ? "Copied!" : "Copy a link to this view"} arrow>
              <Button type="button" size="small" variant="outlined" className="stat-filter-action-button" startIcon={copied ? <CheckIcon /> : <IosShareOutlinedIcon />} aria-label={copied ? "Link copied" : "Share"} onClick={shareView}>
                <span className="stat-filter-action-label">{copied ? "Copied!" : "Share"}</span>
              </Button>
            </Tooltip>
            <Button type="button" size="small" variant={expanded ? "contained" : "outlined"} disableElevation className="stat-filter-toggle" startIcon={<FilterAltOutlinedIcon />} aria-label={`Filters${activeCount ? ` (${activeCount} active)` : ""}`} aria-expanded={expanded} onClick={() => setExpanded((current) => !current)}>
              <span className="stat-filter-action-label">Filters{activeCount ? ` (${activeCount})` : ""}</span>
            </Button>
          </div>
        </div>
        <div className={`stat-filter-grid${expanded ? " open" : ""}`}>
          {!monthlyMode ? <DateRangePickerField size="small" label="Period" dateFrom={filters.dateFrom} dateTo={filters.dateTo} startAdornment={fieldAdornment(<CalendarTodayOutlinedIcon fontSize="small" />)} onChange={(dateFrom, dateTo) => onChange({ ...filters, dateFrom, dateTo, bucket: recommendedStatisticsBucket(dateFrom, dateTo) })} /> : null}
          {!monthlyMode ? <FilterSelect label="Group by" value={filters.bucket} startAdornment={fieldAdornment(<ViewWeekOutlinedIcon fontSize="small" />)} onChange={(value) => onChange({ ...filters, bucket: value as StatisticsFilters["bucket"] })}>
            <MenuItem value="day">Day</MenuItem><MenuItem value="week">Week</MenuItem><MenuItem value="month">Month</MenuItem>
          </FilterSelect> : null}
          <FilterSelect label="Compare with" value={filters.compare} startAdornment={fieldAdornment(<CompareArrowsOutlinedIcon fontSize="small" />)} onChange={(value) => onChange({ ...filters, compare: value as StatisticsFilters["compare"] })}>
            <MenuItem value="previous">Previous period</MenuItem><MenuItem value="year">Same period last year</MenuItem>
          </FilterSelect>
          {!monthlyMode ? <FilterSelect label="Type" value={filters.type} displayEmpty startAdornment={fieldAdornment(<SwapVertOutlinedIcon fontSize="small" />)} onChange={(value) => onChange({ ...filters, type: value as StatisticsFilters["type"] })}>
            <MenuItem value="">All</MenuItem><MenuItem value="expense">Expense</MenuItem><MenuItem value="income">Income</MenuItem><MenuItem value="transfer">Transfer</MenuItem>
          </FilterSelect> : null}
          {!monthlyMode ? <CategorySearchSelect size="small" label="Category" value={filters.categoryId} emptyLabel="All categories" categories={categories} allCategories={categories} disableMobileSearchAutoFocus startAdornment={fieldAdornment(<CategoryOutlinedIcon fontSize="small" />)} onChange={(categoryId) => onChange({ ...filters, categoryId })} /> : null}
          <UserSelect size="small" className="stat-user-filter-select" label="Person" members={members} emptyLabel="All people" filterDropdown value={filters.userId} startAdornment={fieldAdornment(<PersonOutlineOutlinedIcon fontSize="small" />)} onChange={(userId) => onChange({ ...filters, userId })} />
          <AccountSelect size="small" label="Account" accounts={accounts} members={members} emptyLabel="All accounts" filterDropdown value={filters.accountId} startAdornment={fieldAdornment(<AccountBalanceWalletOutlinedIcon fontSize="small" />)} onChange={(accountId) => onChange({ ...filters, accountId })} />
          <TagFilterSelect tags={tags} value={filters.tagIds} startAdornment={fieldAdornment(<LocalOfferOutlinedIcon fontSize="small" />)} onChange={(tagIds) => onChange({ ...filters, tagIds })} />
          {!monthlyMode ? <TextField size="small" label="Payee" value={merchantInput} slotProps={{ input: { startAdornment: fieldAdornment(<StorefrontOutlinedIcon fontSize="small" />), endAdornment: merchantInput ? <InputAdornment position="end"><IconButton size="small" edge="end" aria-label="Clear payee" title="Clear payee" onClick={() => onMerchantInput("")}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null } }} onChange={(event) => onMerchantInput(event.target.value)} /> : null}
          <Button type="button" variant="outlined" className="stat-filter-reset" startIcon={<RestartAltOutlinedIcon />} disabled={!canReset} onClick={onReset}>Reset filters</Button>
        </div>
    </>
  );
  if (compact) return <div className="stat-filter-compact">{content}</div>;
  return <Card className="records-panel stat-filter-card"><CardContent className="records-panel-content">{content}</CardContent></Card>;
}
